import type { Reference } from "../types/documentTypes";

const FieldTypesEnum = {
    Applicants: "applicants",
    Incomes: "incomes",
    Employments: "employments",
    Expenses: "expenses",
    Assets: "assets",
    Liabilities: "liabilities",
} as const;

type FieldTypesEnum = (typeof FieldTypesEnum)[keyof typeof FieldTypesEnum];

const ValidationTypeEnum = {
    Error: "error",
    Warning: "warning",
} as const;

type ValidationTypeEnum = (typeof ValidationTypeEnum)[keyof typeof ValidationTypeEnum];

const MenuBagdeConfigs = [
    {
        page: "applicants",
        step: 5,
        field: FieldTypesEnum.Applicants,
        elements: (form: any) => form?.state?.values?.applicants || [],
    },
    {
        page: "employments",
        step: 6,
        field: FieldTypesEnum.Employments,
        elements: (form: any) => form?.state?.values?.employments || [],
    },
    {
        page: "incomes",
        step: 7,
        field: FieldTypesEnum.Incomes,
        elements: (form: any) => form?.state?.values?.incomes || [],
    },
    {
        page: "expenses",
        step: 8,
        field: FieldTypesEnum.Expenses,
        elements: (_: any) => [], // Expenses are not tracked for errors/warnings
    },
    {
        page: "assets",
        step: 9,
        field: FieldTypesEnum.Assets,
        elements: (_: any) => [], // Assets are not tracked for errors/warnings
    },
    {
        page: "liabilities",
        step: 10,
        field: FieldTypesEnum.Liabilities,
        elements: (_: any) => [], // Liabilities are not tracked for errors/warnings
    },
];

const getAllWarningMessagesForType = (form: any, type: ValidationTypeEnum) => {
    let result = 0;
    MenuBagdeConfigs.map((config) => {
        config.elements(form).forEach((element: any, _: number) => {
            const { warnings, errors } = getFieldsValidation(element);
            result += type === ValidationTypeEnum.Warning ? warnings : errors;
        });
    });
    return result;
};

const documentValidationForReference = (reference?: Reference) => {
    const hasWarning = reference?.validationStatus === "warning";
    const hasError = reference?.validationStatus === "error";

    return {
        hasWarning,
        hasError,
        validationMessage: reference?.validationStatusReason || "",
    };
};

type FieldValidationsForIndex = {
    errors: number;
    warnings: number;
    badge: number | undefined;
    badgeVariant: "error" | "warning" | undefined;
};

const getAllValidationTypeForReference = (references: Map<string, Reference>, type: ValidationTypeEnum) => {
    return Array.from(references.values())
        .filter((r) => r?.validationStatus === type && r.validationStatusReason)
        .map((r) => r.validationStatusReason!);
};

const getFieldsValidation = (object: any): FieldValidationsForIndex => {
    // default case to avoid errors
    if (object === null || object === undefined) {
        return {
            errors: 0,
            warnings: 0,
            badge: undefined,
            badgeVariant: undefined,
        };
    }

    let errors = 0;
    let warnings = 0;

    if (Array.isArray(object)) {
        object.forEach((item: any) => {
            const validations = getFieldsValidation(item);
            errors += validations.errors;
            warnings += validations.warnings;
        });
    } else {
        const references = object.references || new Map();
        errors += getAllValidationTypeForReference(references, ValidationTypeEnum.Error).length;
        warnings += getAllValidationTypeForReference(references, ValidationTypeEnum.Warning).length;
        for (const key in object) {
            if (key !== "references" && typeof object[key] === "object" && object[key] !== null) {
                const nestedValidations = getFieldsValidation(object[key]);
                errors += nestedValidations.errors;
                warnings += nestedValidations.warnings;
            }
        }
    }
    return {
        errors,
        warnings,
        badge: errors > 0 ? errors : warnings > 0 ? warnings : undefined,
        badgeVariant: errors ? "error" : warnings ? "warning" : undefined,
    };
};

export {
    getFieldsValidation,
    documentValidationForReference,
    getAllWarningMessagesForType,
    MenuBagdeConfigs,
    FieldTypesEnum,
};
